<template>
  <div id="app">
    <transition mode="out-in" name="slide-fade">
      <div v-if="!signedIn" :key="'signedIn_' + signedIn">
        <b-card class="shadow-sm mt-5" :class="{'min-width-400': $store.state.currentBreakpoint !== 'xs'}" border-variant="light">

          <div class="d-flex justify-content-center mt-3">
            <b-img :src="app.logo"></b-img>
          </div>

          <b-card-title title-tag="h5" class="text-center mt-3 text-thinner mb-0 text-uppercase">
            {{app.name}}
          </b-card-title>
          <b-card-sub-title class="text-center mt-1 text-thinner mb-0">
            <small>Developed by <a href="https://thunderquote.com" target="_blank">ThunderQuote</a></small>
          </b-card-sub-title>
          

          <b-card-body>
            <transition mode="out-in" name="slide-fade">
              <div v-if="mode == 'signIn'" :key="mode">
                <h6 class="text-center mb-4 text-thinner">SIGN IN</h6>
                <div>
                  <b-alert variant="danger" dismissible :show="!!signIn.error" @dismissed="signIn.error=null">
                    <div class="max-width-300">
                      <strong>Error: </strong>
                      <span class="m-0">{{parsedSignInError}}</span>
                    </div>
                    </b-alert>
                </div>
                <b-input-group size="md">
                  <b-input-group-prepend is-text>
                    <b-icon icon="envelope"></b-icon>
                  </b-input-group-prepend>
                  <b-form-input
                    @keypress.enter="signInSubmit"
                    v-model="signIn.email"
                    autofocus 
                    type="email" 
                    placeholder="Email address">
                  </b-form-input>
                </b-input-group>

                <b-input-group size="md" class="mt-2 mb-3">
                  <b-input-group-prepend is-text>
                    <b-icon icon="key"></b-icon>
                  </b-input-group-prepend>
                  <b-form-input 
                    @keypress.enter="signInSubmit" 
                    v-model="signIn.password" 
                    type="password" 
                    placeholder="Password">
                  </b-form-input>
                </b-input-group>

                <div>
                  <small>Forgot your password? </small><small @click="mode='resetPassword'" class="text-primary fake-link">Reset password</small>
                </div>
                <div>
                  <b-button block variant="primary" squared class="mt-4" :disabled="!canSignIn || signingIn" @click="signInSubmit">
                    <b-icon v-if="signingIn" icon="arrow-clockwise" animation="spin" font-scale="1"></b-icon>
                    Sign in
                  </b-button>
                </div>
              </div>
              <div v-else-if="mode == 'resetPassword'" :key="mode">
                <h6 class="text-center mb-4 text-thinner">RESET PASSWORD</h6>
                <div>
                  <b-alert variant="danger" dismissible :show="!!resetPassword.error" @dismissed="resetPassword.error=null">
                    <div class="max-width-300">
                      <strong>Error: </strong>
                      <span class="m-0">{{parsedResetPasswordError}}</span>
                    </div>
                    </b-alert>
                </div>
                <b-input-group size="md">
                  <b-input-group-prepend is-text>
                    <b-icon icon="envelope"></b-icon>
                  </b-input-group-prepend>
                  <b-form-input
                    @keypress.enter="resetPasswordSubmit"
                    v-model="resetPassword.email"
                    autofocus 
                    type="email" 
                    placeholder="Email address">
                  </b-form-input>
                </b-input-group>

                <b-button block variant="primary" squared class="mt-4" :disabled="resettingPassword || !resetPassword.email" @click="resetPasswordSubmit">
                  <b-icon v-if="resettingPassword" icon="arrow-clockwise" animation="spin" font-scale="1"></b-icon>
                    Send code
                  </b-button>
                <b-button block variant="outline-primary" squared class="mt-2" @click="backToSignIn">
                  Back
                </b-button>
              </div>
              <div v-else-if="mode == 'changePassword'" :key="mode">
                <h6 class="text-center mb-4 text-thinner">CHANGE PASSWORD</h6>
                <div>
                  <b-alert variant="success" dismissible :show="codeSent" @dismissed="codeSent=false">
                    <div class="max-width-300">
                      <span class="m-0">Please check your registered email for the verification code to reset your password.</span>
                    </div>
                  </b-alert>
                  <b-alert variant="danger" dismissible :show="!!changePassword.error" @dismissed="changePassword.error=null">
                    <div class="max-width-300">
                      <strong>Error: </strong>
                      <span class="m-0">{{parsedChangePasswordError}}</span>
                    </div>
                  </b-alert>
                </div>
                <b-input-group size="md">
                  <b-input-group-prepend is-text>
                    <b-icon icon="lock"></b-icon>
                  </b-input-group-prepend>
                  <b-form-input
                    @keypress.enter="changePasswordSubmit"
                    autofocus
                    v-model="changePassword.code"
                    placeholder="Verification code">
                  </b-form-input>
                </b-input-group>

                <b-input-group size="md" class="mt-1 mb-3">
                  <b-input-group-prepend is-text>
                    <b-icon icon="key"></b-icon>
                  </b-input-group-prepend>
                  <b-form-input 
                    @keypress.enter="changePasswordSubmit" 
                    v-model="changePassword.password" 
                    type="password" 
                    placeholder="New password">
                  </b-form-input>
                </b-input-group>

                <div>
                  <b-button block variant="primary" squared class="mt-4" :disabled="!canChangePassword || changingPassword" @click="changePasswordSubmit">
                    <b-icon v-if="changingPassword" icon="arrow-clockwise" animation="spin" font-scale="1"></b-icon>
                    Change Password
                  </b-button>
                  <b-button block variant="outline-primary" squared class="mt-2" @click="backToSignIn">
                    Back to Sign In
                  </b-button>
                </div>
              </div>
              <div v-else-if="mode == 'firstTimeChangePassword'" :key="mode">
                <h6 class="text-center text-thinner">FIRST TIME LOGIN
                  <h6 class="text-center mb-4 text-thinner">PASSWORD CHANGE REQUIRED</h6>
                </h6>
                <div>
                  <b-alert variant="danger" dismissible :show="!!firstTimeChangePassword.error" @dismissed="firstTimeChangePassword.error=null">
                    <div class="max-width-300">
                      <strong>Error: </strong>
                      <span class="m-0">{{parsedFirstTimeChangePasswordError}}</span>
                    </div>
                  </b-alert>
                </div>
                <b-input-group size="md">
                  <b-input-group-prepend is-text>
                    <b-icon icon="key"></b-icon>
                  </b-input-group-prepend>
                  <b-form-input 
                    @keypress.enter="firstTimeChangePasswordSubmit" 
                    v-model="firstTimeChangePassword.password"
                    autofocus 
                    type="password" 
                    placeholder="New password">
                  </b-form-input>
                </b-input-group>

                <b-input-group size="md" class="mt-1 mb-3">
                  <b-input-group-prepend is-text>
                    <b-icon icon="key-fill"></b-icon>
                  </b-input-group-prepend>
                  <b-form-input 
                    @keypress.enter="firstTimeChangePasswordSubmit" 
                    v-model="firstTimeChangePassword.confirmPassword" 
                    type="password" 
                    placeholder="New password again">
                  </b-form-input>
                </b-input-group>

                <div class="text-center" v-if="firstTimeChangePassword.password !== firstTimeChangePassword.confirmPassword">
                  <small class="text-danger">Password does not match</small>
                </div>

                <div>
                  <b-button block variant="primary" squared class="mt-3" :disabled="!canFirstTimeChangePassword || firstTimeChangingPassword" @click="firstTimeChangePasswordSubmit">
                    <b-icon v-if="firstTimeChangingPassword" icon="arrow-clockwise" animation="spin" font-scale="1"></b-icon>
                    Change Password
                  </b-button>
                  <b-button block variant="outline-primary" squared class="mt-2" @click="backToSignIn">
                    Back to Sign In
                  </b-button>
                </div>
                
              </div>
            </transition>
            <div v-if="signedIn" class="text-center mt-3">
              <p class="m-0">Loading App...</p>
            </div> 
          </b-card-body>
            
        </b-card>
      </div>
      <div v-else class="mt-5">
        <div class="mb-3">
            <div class="d-flex justify-content-center">
                <b-img :src="app.logo"></b-img>
            </div>
            <div>
                <b-card-title title-tag="h5" class="text-center mt-2 text-thinner mb-0 text-uppercase">{{app.name}}</b-card-title>
            </div>
            <b-card-sub-title class="text-center mt-1 text-thinner mb-0">
              <small>Developed by <a href="https://thunderquote.com" target="_blank">ThunderQuote</a></small>
            </b-card-sub-title>
        </div>  
        <div class="d-flex justify-content-end align-items-center my-2">
          <b-button @click="signOutConfirm" :disabled="signingOut" size="sm" variant="outline-secondary">
            <b-icon icon="door-open" class="mr-2"></b-icon>
            Sign out
          </b-button>
        </div>
        <div>
          <main-table></main-table>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>

//import Amplify from 'aws-amplify';
import MainTable from '@/components/Maintable.vue';

export default {
  name: 'App',
  components: {
    MainTable
  },
  async created() {
    const setBp = () => {
      this.$store.commit('setCurrentBreakpoint');
    }
    setBp();

    window.addEventListener("resize", setBp);
    await this.$store.state.amplifyInstance.Auth.currentAuthenticatedUser()
    if (this.$store.state.amplifyInstance.Auth.user) {
      this.$store.commit('signedIn');
    }
  },
  beforeDestroy(){
    window.removeEventListener('resize');
  },
  data() {
    return {
      mode: 'signIn',
      signingIn: false,
      signIn: {
        email: null,
        password: null,
        error: null
      },
      changePassword: {
        code: null,
        password: null,
        error: null
      },
      firstTimeChangePassword: {
        password: '',
        confirmPassword: '',
        error: null
      },
      firstTimeChangingPassword: false,
      changingPassword: false,
      resettingPassword: false,
      resetPassword: {
        email: null,
        error: null
      },
      codeSent: false,
      cognitoUser: null,
      signingOut: false
    }
  },
  computed: {
    app() {
        return this.$store.state.app
    },
    canChangePassword(){
      return this.changePassword.code && this.changePassword.password;
    },
    signedIn() {
      return this.$store.state.signedIn;
    },
    parsedSignInError(){
      let error = this.signIn.error;
      if (error && error.message){
        return error.message;
      }
      return 'Failed to sign in. Please try again.';
    },
    parsedChangePasswordError(){
      let error = this.changePassword.error;
      if (error && error.message){
        return error.message;
      }
      return 'Failed to change password. Please try again.';
    },
    parsedResetPasswordError(){
      let finalError = 'Failed to reset password. Please try again.';
      let error = this.resetPassword.error;

      if (error && error.message){

        finalError = error.message;
        if (error.message == 'Username/client id combination not found.') {
          finalError = 'Account not found.'
        }
        return finalError;

      }
      return finalError;
    },
    parsedFirstTimeChangePasswordError(){
      let finalError = 'Failed to reset password. Please try again.';
      let error = this.firstTimeChangePassword.error;

      if (error && error.message){
        finalError = error.message;
      }
      return finalError;
    },
    canSignIn(){
      return this.signIn.email && this.signIn.password;
    },
    canFirstTimeChangePassword(){
      return (
        (this.firstTimeChangePassword.password && this.firstTimeChangePassword.confirmPassword) &&
        (this.firstTimeChangePassword.password === this.firstTimeChangePassword.confirmPassword)
      );
    }
  },
  methods: {
    signOutConfirm(){
      this.$root.confirm({
                message: 'Are you sure you want to sign out?',
                title: 'Confirm sign out',
                okVariant: 'primary',
                okTitle: 'Confirm',
                cancelTitle: 'Cancel'
            }, () => this.signOut());
    },
    async signOut(){
        this.signingOut = true;
        try {
            await this.$store.state.amplifyInstance.Auth.signOut();
            this.backToSignIn();
            this.$store.commit('signedIn', false);
            this.$bvToast.toast('Signed out successfully!', {
              title: `Signed Out`,
              variant: 'primary',
              solid: true
            });
            this.signingOut = false;
        } catch (error) {
            this.signingOut = false;
            this.$bvToast.toast(error, {
              title: `Error signing out`,
              variant: 'danger',
              solid: true
            });
        }
    },
    changePasswordSuccess(result){
      for (let i in result) return false;
      return true;
    },
    backToSignIn(){
      this.codeSent = false;
      this.signIn.error = null;
      this.firstTimeChangePassword.error = null;
      this.resetPassword.error = null;
      this.changePassword.error = null;
      this.mode = 'signIn';
    },
    async firstTimeChangePasswordSubmit(){
      if (!this.canFirstTimeChangePassword) {
        return;
      }
      this.firstTimeChangingPassword = true;
      this.firstTimeChangePassword.error = null;
      try {
        await this.$store.state.amplifyInstance.Auth.completeNewPassword(this.cognitoUser, this.firstTimeChangePassword.password);
        this.$store.commit('signedIn');
        this.cognitoUser = null;
        this.$bvToast.toast('Password has been changed and you have been signed in!', {
          title: `Success`,
          variant: 'primary',
          solid: true
        });
        this.firstTimeChangingPassword = false;
      } catch(error) {
        this.firstTimeChangePassword.error = error;
        this.firstTimeChangingPassword = false;
      }
    },
    async resetPasswordSubmit() {
      if (!this.resetPassword.email) {
        return;
      }
      this.codeSent = false;
      this.resetPassword.error = null;

      try {
        this.resettingPassword = true;
        await this.$store.state.amplifyInstance.Auth.forgotPassword(this.resetPassword.email);
        this.resettingPassword = false;
        this.codeSent = true;
        this.mode = 'changePassword';
      } catch (error) {
          this.codeSent = false;
          this.resetPassword.error = error;
          this.resettingPassword = false;
      }
    },
    async changePasswordSubmit(){
      if (!this.canChangePassword) {
        return;
      }
      this.changingPassword = true;
      this.changePassword.error = null;
      try {
        await this.$store.state.amplifyInstance.Auth.forgotPasswordSubmit(this.resetPassword.email, this.changePassword.code, this.changePassword.password);
        this.changingPassword = false;
        this.backToSignIn();
        this.$bvToast.toast('Password has been changed. You may now sign in with the new password.', {
          title: `Success`,
          variant: 'primary',
          solid: true
        });
      } catch (error) {
        this.changingPassword = false;
        this.changePassword.error = error;
      }
    },
    async signInSubmit(){
      if (!this.canSignIn) {
        return;
      }
      this.signIn.error = null;

      try {
        this.signingIn = true;
        const user = await this.$store.state.amplifyInstance.Auth.signIn(this.signIn.email, this.signIn.password);

        if (this.$store.state.amplifyInstance.Auth.user) {
          this.$store.commit('signedIn');
          this.$store.commit('signIn', this.signIn);
        }
        if (user.challengeName && user.challengeName == 'NEW_PASSWORD_REQUIRED') {
          this.cognitoUser = user;
          this.mode = "firstTimeChangePassword";
        }

        this.signingIn = false;
      } catch (error) {
          this.signIn.error = error;
          this.signingIn = false;
      }
    },
  }
}
</script>

<style lang="scss">

  $primary: #655aaf;
  $font-family-sans-serif: 'montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue',
    Arial, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol';
  @import 'node_modules/bootstrap/scss/bootstrap';
  @import 'node_modules/bootstrap-vue/src/index.scss';


  body {
    height: 100vh;
    background-color: #ffffff;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 304 304' width='304' height='304'%3E%3Cpath fill='%23655aaf' fill-opacity='0.03' d='M44.1 224a5 5 0 1 1 0 2H0v-2h44.1zm160 48a5 5 0 1 1 0 2H82v-2h122.1zm57.8-46a5 5 0 1 1 0-2H304v2h-42.1zm0 16a5 5 0 1 1 0-2H304v2h-42.1zm6.2-114a5 5 0 1 1 0 2h-86.2a5 5 0 1 1 0-2h86.2zm-256-48a5 5 0 1 1 0 2H0v-2h12.1zm185.8 34a5 5 0 1 1 0-2h86.2a5 5 0 1 1 0 2h-86.2zM258 12.1a5 5 0 1 1-2 0V0h2v12.1zm-64 208a5 5 0 1 1-2 0v-54.2a5 5 0 1 1 2 0v54.2zm48-198.2V80h62v2h-64V21.9a5 5 0 1 1 2 0zm16 16V64h46v2h-48V37.9a5 5 0 1 1 2 0zm-128 96V208h16v12.1a5 5 0 1 1-2 0V210h-16v-76.1a5 5 0 1 1 2 0zm-5.9-21.9a5 5 0 1 1 0 2H114v48H85.9a5 5 0 1 1 0-2H112v-48h12.1zm-6.2 130a5 5 0 1 1 0-2H176v-74.1a5 5 0 1 1 2 0V242h-60.1zm-16-64a5 5 0 1 1 0-2H114v48h10.1a5 5 0 1 1 0 2H112v-48h-10.1zM66 284.1a5 5 0 1 1-2 0V274H50v30h-2v-32h18v12.1zM236.1 176a5 5 0 1 1 0 2H226v94h48v32h-2v-30h-48v-98h12.1zm25.8-30a5 5 0 1 1 0-2H274v44.1a5 5 0 1 1-2 0V146h-10.1zm-64 96a5 5 0 1 1 0-2H208v-80h16v-14h-42.1a5 5 0 1 1 0-2H226v18h-16v80h-12.1zm86.2-210a5 5 0 1 1 0 2H272V0h2v32h10.1zM98 101.9V146H53.9a5 5 0 1 1 0-2H96v-42.1a5 5 0 1 1 2 0zM53.9 34a5 5 0 1 1 0-2H80V0h2v34H53.9zm60.1 3.9V66H82v64H69.9a5 5 0 1 1 0-2H80V64h32V37.9a5 5 0 1 1 2 0zM101.9 82a5 5 0 1 1 0-2H128V37.9a5 5 0 1 1 2 0V82h-28.1zm16-64a5 5 0 1 1 0-2H146v44.1a5 5 0 1 1-2 0V18h-26.1zm102.2 270a5 5 0 1 1 0 2H98v14h-2v-16h124.1zM242 149.9V160h16v34h-16v62h48v48h-2v-46h-48v-66h16v-30h-16v-12.1a5 5 0 1 1 2 0zM53.9 18a5 5 0 1 1 0-2H64V2H48V0h18v18H53.9zm112 32a5 5 0 1 1 0-2H192V0h50v2h-48v48h-28.1zm-48-48a5 5 0 0 1-9.8-2h2.07a3 3 0 1 0 5.66 0H178v34h-18V21.9a5 5 0 1 1 2 0V32h14V2h-58.1zm0 96a5 5 0 1 1 0-2H137l32-32h39V21.9a5 5 0 1 1 2 0V66h-40.17l-32 32H117.9zm28.1 90.1a5 5 0 1 1-2 0v-76.51L175.59 80H224V21.9a5 5 0 1 1 2 0V82h-49.59L146 112.41v75.69zm16 32a5 5 0 1 1-2 0v-99.51L184.59 96H300.1a5 5 0 0 1 3.9-3.9v2.07a3 3 0 0 0 0 5.66v2.07a5 5 0 0 1-3.9-3.9H185.41L162 121.41v98.69zm-144-64a5 5 0 1 1-2 0v-3.51l48-48V48h32V0h2v50H66v55.41l-48 48v2.69zM50 53.9v43.51l-48 48V208h26.1a5 5 0 1 1 0 2H0v-65.41l48-48V53.9a5 5 0 1 1 2 0zm-16 16V89.41l-34 34v-2.82l32-32V69.9a5 5 0 1 1 2 0zM12.1 32a5 5 0 1 1 0 2H9.41L0 43.41V40.6L8.59 32h3.51zm265.8 18a5 5 0 1 1 0-2h18.69l7.41-7.41v2.82L297.41 50H277.9zm-16 160a5 5 0 1 1 0-2H288v-71.41l16-16v2.82l-14 14V210h-28.1zm-208 32a5 5 0 1 1 0-2H64v-22.59L40.59 194H21.9a5 5 0 1 1 0-2H41.41L66 216.59V242H53.9zm150.2 14a5 5 0 1 1 0 2H96v-56.6L56.6 162H37.9a5 5 0 1 1 0-2h19.5L98 200.6V256h106.1zm-150.2 2a5 5 0 1 1 0-2H80v-46.59L48.59 178H21.9a5 5 0 1 1 0-2H49.41L82 208.59V258H53.9zM34 39.8v1.61L9.41 66H0v-2h8.59L32 40.59V0h2v39.8zM2 300.1a5 5 0 0 1 3.9 3.9H3.83A3 3 0 0 0 0 302.17V256h18v48h-2v-46H2v42.1zM34 241v63h-2v-62H0v-2h34v1zM17 18H0v-2h16V0h2v18h-1zm273-2h14v2h-16V0h2v16zm-32 273v15h-2v-14h-14v14h-2v-16h18v1zM0 92.1A5.02 5.02 0 0 1 6 97a5 5 0 0 1-6 4.9v-2.07a3 3 0 1 0 0-5.66V92.1zM80 272h2v32h-2v-32zm37.9 32h-2.07a3 3 0 0 0-5.66 0h-2.07a5 5 0 0 1 9.8 0zM5.9 0A5.02 5.02 0 0 1 0 5.9V3.83A3 3 0 0 0 3.83 0H5.9zm294.2 0h2.07A3 3 0 0 0 304 3.83V5.9a5 5 0 0 1-3.9-5.9zm3.9 300.1v2.07a3 3 0 0 0-1.83 1.83h-2.07a5 5 0 0 1 3.9-3.9zM97 100a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm0-16a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm16 16a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm16 16a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm0 16a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm-48 32a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm16 16a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm32 48a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm-16 16a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm32-16a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm0-32a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm16 32a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm32 16a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm0-16a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm-16-64a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm16 0a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm16 96a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm0 16a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm16 16a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm16-144a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm0 32a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm16-32a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm16-16a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm-96 0a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm0 16a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm16-32a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm96 0a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm-16-64a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm16-16a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm-32 0a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm0-16a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm-16 0a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm-16 0a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm-16 0a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM49 36a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm-32 0a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm32 16a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM33 68a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm16-48a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm0 240a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm16 32a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm-16-64a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm0 16a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm-16-32a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm80-176a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm16 0a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm-16-16a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm32 48a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm16-16a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm0-32a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm112 176a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm-16 16a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm0 16a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm0 16a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM17 180a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm0 16a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm0-32a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm16 0a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM17 84a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm32 64a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm16-16a3 3 0 1 0 0-6 3 3 0 0 0 0 6z'%3E%3C/path%3E%3C/svg%3E");
  }

  #app {
    display: flex;
    height: 100%;
    justify-content: center;
  }

  .form-group {
    margin-top: 5px;
    margin-bottom: 5px;
  }

  .input-group-text {
    background-color: #fff;
  }

  .fake-link:hover {
    cursor: pointer;
    text-decoration: underline;
  }

  .text-thinner {
    font-weight: 350;
  }

  .min-width-400 {
    min-width: 400px;
  }

  .max-width-300 {
    max-width: 300px;
  }

  *:focus
  {
    outline: none !important;
  }

  .slide-fade-enter-active {
    transition: all .3s ease;
  }

  .slide-fade-leave-active {
    transition: all .3s;
  }

  .slide-fade-enter, .slide-fade-leave-to {
    transform: translateX(10px);
    opacity: 0;
  }
</style>
